import { dataBindingInspectorPanel } from '@wix/wix-data-client-common/dist/esm/panels/panelDefs'
import getPanelUrl from '@wix/wix-data-client-common/dist/esm/panels/getPanelUrl'
import {
  promiseOrValueAfter,
  DESIGN_SYSTEMS,
} from '@wix/wix-data-client-common-standalone'
import {
  getComponentApplicationId,
  isDataset,
} from '@wix/wix-data-client-common'
import type {
  AppApi,
  EDITOR_TYPE,
  ViewDescriptor,
} from '@wix/wix-data-client-common'
import { logOpenConnectionPanelToBi } from './worker/workerBiEventsLogger'
import { getComponentTypeData } from './business-logic/components/getComponentTypeData'
import type { EditorSDK, Logger } from '@wix/wix-data-client-common-standalone'
import type { ComponentRef } from '@wix/editor-platform-sdk-types'
import { getBindingDefinitions } from './business-logic/bindings/componentBindingInfo'

interface OpenPanelProps {
  hostname: string
  appApi: AppApi
  editorSdkProxy: EditorSDK
  logger: Logger
  editorType: EDITOR_TYPE
  initiator: string
  origin: string
}

const panel = {
  isOpen: false,
}

export const isInspectorPanelOpen = async (appApi: AppApi) => {
  if (!panel.isOpen) {
    return false
  }

  appApi.emit('requestIsPanelOpen', { panelName: 'inspectorPanel' })

  let cleanup: () => void
  const isPanelOpen = new Promise<boolean>(resolve => {
    const handlePanelResponse = ({ panelName }: { panelName: string }) => {
      if (panelName === 'inspectorPanel') {
        resolve(true)
      }
    }

    const token = appApi.addListener('responseIsPanelOpen', handlePanelResponse)
    cleanup = () => appApi.removeListener('responseIsPanelOpen', token)
  })

  panel.isOpen = await promiseOrValueAfter({
    promise: isPanelOpen,
    timeout: 500,
    value: false,
  })
  cleanup!()
  return panel.isOpen
}

export const openClassicInspectorPanel = async (
  {
    hostname,
    appApi,
    editorSdkProxy,
    logger,
    editorType,
    initiator,
    origin,
  }: OpenPanelProps,
  {
    componentRef,
    initialView,
  }: { componentRef: ComponentRef; initialView?: ViewDescriptor },
) => {
  appApi.disallowCollectionSchemaRefresh()
  try {
    const [
      { sdkType: componentType, viewerType: componentViewerType },
      applicationId,
    ] = await Promise.all([
      getComponentTypeData({
        editorSdkProxy,
        componentRef,
      }),
      getComponentApplicationId(editorSdkProxy, componentRef),
    ])

    const maybeBindingDefinition = await getBindingDefinitions({
      editorSdkProxy,
      componentRef,
      componentType,
      componentViewerType,
      appApi,
    })

    const componentIsConnectable =
      Boolean(maybeBindingDefinition.value) ||
      isDataset(componentType, applicationId)
    if (!componentIsConnectable) {
      return
    }

    if (!componentRef) {
      return
    }

    logOpenConnectionPanelToBi(logger, editorSdkProxy, {
      componentRef,
      componentType: componentViewerType,
    })

    const panelResult = openPanel(
      { hostname, editorSdkProxy },
      {
        componentRef,
        componentType,
        initiator,
        origin,
        editorType,
        initialView,
      },
    )
    panel.isOpen = true
    await panelResult
  } finally {
    panel.isOpen = false
    appApi.allowCollectionSchemaRefresh()
  }
}

async function openPanel(
  {
    hostname,
    editorSdkProxy,
  }: {
    hostname: string
    editorSdkProxy: EditorSDK
  },
  {
    componentRef,
    componentType,
    initiator,
    origin,
    editorType,
    initialView,
  }: {
    componentRef: ComponentRef
    componentType: string
    initiator: string
    origin: string
    editorType: EDITOR_TYPE
    initialView?: ViewDescriptor
  },
) {
  const url = await getPanelUrl({
    hostname,
    relativeUrl: dataBindingInspectorPanel.url,
    editorSdkProxy,
    params: {
      designSystemsToLoad: [DESIGN_SYSTEMS.WDS, DESIGN_SYSTEMS.BASE_UI],
    },
  })

  const initialLoadTimeForBI = Date.now()
  const panelResult = await editorSdkProxy.editor.openSidePanel({
    url,
    initialData: {
      componentRef,
      componentType,
      initialLoadTimeForBI,
      initiator,
      origin,
      editorType,
      initialView,
    },
    displayAboveModals: false,
    //@ts-expect-error TODO: add this prop to editor-patform-sdk-types
    type: 'connect',
  })

  editorSdkProxy.editor.selection.clearHighlights()
  return panelResult
}
